import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  CustomCollapseCard,
  CustomForm,
  CustomFormItem,
  CustomSelect,
  CustomImage,
  Option,
  Text,
  useText,
  warningDialog,
} from '@src/components';

import {
  setTotalAutoLoading,
  updateAutoLoadingCommand,
} from '@src/store/slice/autoUploadInstructionsSlice';

import { monthsList } from '@src/store/enums';
import dayjs from '@src/utils/day';

import { Radio, Tooltip, Alert, Form } from 'antd';
import warning from '@src/assets/icons/icon-profil-warning.svg';
import '@src/styles/autoUploadInstruction/addInformation.scss';

import { useMediaQuery } from '@src/hooks/useMediaQuery';

const AdditionalInformation = () => {
  const isMobil = useMediaQuery({ query: '(max-width: 1199.98px)' });

  const dispatch = useDispatch();
  const currentDate = dayjs();
  const [form] = Form.useForm();
  const [form2] = Form.useForm();

  const selectChoose = useText('choose');
  const attentionText = useText('attention');
  const continueText = useText('continue');

  const { totalAutoLoading, autoLoadingId, integrationId } = useSelector(
    (state) => state?.autoUploadInstructions,
  );
  const [selectedStartDay, setSelectedStartDay] = useState(null);

  const [tooltipVisible, setTooltipVisible] = useState(false);

  const updateAutoLoading = useCallback(
    async ({ startDay, confirmEveryMonth, chooseMonth }) => {
      const currentDay = currentDate?.date();

      const data = {
        id: totalAutoLoading?.id,
        periodType: totalAutoLoading?.periodType,
        confirmEveryMonth: confirmEveryMonth,
        startDay: startDay,
        isActive: totalAutoLoading?.isActive || true,
        orderProcessType: totalAutoLoading?.orderProcessType,
      };
      if (autoLoadingId && autoLoadingId > 0) {
        data.id = autoLoadingId;
      }

      if (integrationId && integrationId > 0) {
        data.integrationId = integrationId;
      }
      data.monthChoice = currentDay < startDay ? chooseMonth : false;

      await dispatch(updateAutoLoadingCommand(data));
    },
    [totalAutoLoading, autoLoadingId, integrationId],
  );

  const onChangeConfirmMonth = useCallback(
    (e) => {
      dispatch(
        setTotalAutoLoading({
          ...totalAutoLoading,
          confirmEveryMonth: e.target.value,
        }),
      );

      updateAutoLoading({
        confirmEveryMonth: e.target.value,
        startDay: selectedStartDay,
        chooseMonth: totalAutoLoading?.chooseMonth,
      });
    },
    [totalAutoLoading, selectedStartDay],
  );

  const onWhichDay = useCallback(
    async (e) => {
      const currentDay = currentDate?.date();
      const currentMonth = currentDate?.month() + 1;
      const nextMonth = currentMonth === 12 ? 1 : currentMonth;

      setSelectedStartDay(e);
      setTooltipVisible(true);

      const changeOrUpdate = async () => {
        const values = await form2.validateFields();

        if (values.chooseMonth === null) {
          setSelectedStartDay(0);
          await dispatch(
            setTotalAutoLoading({
              ...totalAutoLoading,
              startDay: 0,
              chooseMonth: false,
            }),
          );

          updateAutoLoading({
            confirmEveryMonth: totalAutoLoading?.confirmEveryMonth,
            startDay: 0,
            chooseMonth: false,
          });
        } else {
          await dispatch(
            setTotalAutoLoading({
              ...totalAutoLoading,
              startDay: e,
              chooseMonth: values.chooseMonth,
            }),
          );

          updateAutoLoading({
            confirmEveryMonth: totalAutoLoading?.confirmEveryMonth,
            startDay: e,
            chooseMonth: values.chooseMonth,
          });
        }
      };

      if (currentDay < e) {
        setTooltipVisible(false);
        await form2.setFieldsValue({
          chooseMonth: null,
        });

        warningDialog({
          title: attentionText,
          okText: continueText,
          className: 'autoUploadInstructions-additionalInformation-custom-month-dialog',
          htmlContent: (
            <CustomForm name="additionalInformation" form={form2}>
              <CustomFormItem name="chooseMonth">
                <Radio.Group
                  id="ticketRestaurant-autoUploadInstructions-instructionInformations-additionalInformation-confirmEveryMonth-radioGroup"
                  className="form-item-radio-area-radioGroup"
                >
                  <Radio
                    id="ticketRestaurant-autoUploadInstructions-instructionInformations-additionalInformation-notconfirmEveryMonth-radio"
                    className="form-item-radio-area-radioGroup-radio-notOkey"
                    value={true}
                  >
                    <div className="confirm-label">
                      <span>
                        <Text t="autoLoadingInstructionValidFromMonthPrefix" />{' '}
                        <Text t={monthsList[nextMonth - 1]?.text?.props?.t} />{' '}
                        <Text t="autoLoadingInstructionValidFromMonthSuffix" />
                      </span>
                    </div>
                  </Radio>

                  <Radio
                    id="ticketRestaurant-autoUploadInstructions-instructionInformations-additionalInformation-confirmEveryMonth-radio"
                    className="form-item-radio-area-radioGroup-radio-okey"
                    style={{ marginTop: '10px' }}
                    value={false}
                  >
                    <div className="confirm-label">
                      <span>
                        <Text t="autoLoadingInstructionValidFromMonthPrefix" />{' '}
                        <Text t={monthsList[nextMonth]?.text?.props?.t} />{' '}
                        <Text t="autoLoadingInstructionValidFromMonthSuffix" />
                      </span>
                    </div>
                  </Radio>
                </Radio.Group>
              </CustomFormItem>
            </CustomForm>
          ),
          onOk: () => {
            changeOrUpdate();
          },
          onCancel: async () => {
            setSelectedStartDay(0);
            await dispatch(
              setTotalAutoLoading({
                ...totalAutoLoading,
                startDay: 0,
                chooseMonth: false,
              }),
            );

            await updateAutoLoading({
              confirmEveryMonth: totalAutoLoading?.confirmEveryMonth,
              startDay: 0,
              chooseMonth: false,
            });
          },
        });
      } else {
        await dispatch(
          setTotalAutoLoading({
            ...totalAutoLoading,
            startDay: e,
            chooseMonth: false,
          }),
        );

        await updateAutoLoading({
          confirmEveryMonth: totalAutoLoading?.confirmEveryMonth,
          startDay: e,
          chooseMonth: false,
        });
      }
    },
    [totalAutoLoading, selectedStartDay, dispatch],
  );

  const selectDay = useCallback(() => {
    const currentDay = currentDate?.date();

    if (currentDay === 0 || totalAutoLoading?.startDay === 0) {
      return null;
    }
    if (currentDay > totalAutoLoading?.startDay) {
      return (
        <div>
          <CustomImage src={warning} />
          <span>
            <Text t="sinceYourSelectedBalanceActivationDateIsEarlier" />{' '}
            {totalAutoLoading?.startDay}
            <Text t="willBeValidFromTheDay" />
          </span>
        </div>
      );
    } else if (currentDay == totalAutoLoading?.startDay) {
      return (
        <div>
          <CustomImage src={warning} />
          <span>
            <Text t="sinceYourSelectedBalanceActivationDateIsThe" /> {totalAutoLoading?.startDay}
            <Text t="willBeValidFromTheDayIfYou" />
          </span>
        </div>
      );
    }
  }, [totalAutoLoading?.startDay]);

  useEffect(async () => {
    form.setFieldsValue({
      confirmEveryMonth: totalAutoLoading?.confirmEveryMonth,
      startDay: totalAutoLoading?.startDay,
    });
    setSelectedStartDay(totalAutoLoading?.startDay);
  }, [form, totalAutoLoading?.startDay, totalAutoLoading?.confirmEveryMonth]);

  const arrayDays = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26,
    27, 28, 29, 30,
  ];

  return (
    <div className="autoUploadInstruction-instructionInformations">
      <CustomCollapseCard
        cardTitle={<Text t="instructionInformations" />}
        className="autoUploadInstruction-instructionInformations-addInformation"
      >
        <div className="add-information-container">
          <div className="autoUploadInstruction-additionalInformation-question">
            <div className="autoUploadInstruction-additionalInformation-question-label">
              <span>
                <Text t="approvalPreference" />
              </span>
            </div>
            <div className="autoUploadInstruction-additionalInformation-question-description">
              <span>
                <Text t="withTheAutomaticLoadingInstructionYouWillCreate" />
              </span>
            </div>
          </div>
          <CustomForm
            name="additionalInformation"
            className="addInformationForm"
            autoComplete="off"
            layout={'horizontal'}
            form={form}
          >
            <div className="form-item-radio-area">
              <CustomFormItem name="confirmEveryMonth">
                <Radio.Group
                  id="ticketRestaurant-autoUploadInstructions-instructionInformations-additionalInformation-confirmEveryMonth-radioGroup"
                  className="form-item-radio-area-radioGroup"
                  onChange={onChangeConfirmMonth}
                  value={totalAutoLoading.confirmEveryMonth}
                  defaultValue={totalAutoLoading.confirmEveryMonth}
                >
                  <Radio
                    id="ticketRestaurant-autoUploadInstructions-instructionInformations-additionalInformation-notconfirmEveryMonth-radio"
                    className="form-item-radio-area-radioGroup-radio-notOkey"
                    value={0}
                    checked={totalAutoLoading.confirmEveryMonth === 0}
                  >
                    <div className="confirm-label">
                      <Text t="iDonTWantToApproveEveryMonth" />
                    </div>
                    <div className="confirm-notOkey-description">
                      <span>
                        <Text t="yourOrderThatYouCreateRegularlyEveryMonth2" />
                      </span>
                    </div>
                  </Radio>

                  <Radio
                    id="ticketRestaurant-autoUploadInstructions-instructionInformations-additionalInformation-confirmEveryMonth-radio"
                    className="form-item-radio-area-radioGroup-radio-okey"
                    value={1}
                    checked={totalAutoLoading.confirmEveryMonth === 1}
                  >
                    <div className="confirm-label">
                      <Text t="iWantToApproveEveryMonth" />
                    </div>
                    <div className="confirm-okey-description">
                      <span>
                        <Text t="yourOrderThatYouCreateRegularlyEveryMonth" />{' '}
                      </span>
                      <span>
                        <Text t="theseOrders" />{' '}
                        <strong>
                          <Text t="myOrdersAutomaticLoadingInstructions" />
                        </strong>{' '}
                        <Text t="youWillNeedToApproveEveryMonthFrom" />
                      </span>
                    </div>
                  </Radio>
                </Radio.Group>
              </CustomFormItem>
            </div>

            <div className="form-item-select-area">
              <div className="start-day-wrapper">
                <span style={{ fontWeight: 500 }}>
                  <Text t={useText('autoActivationDate')} />:
                </span>
                <span className="help-title">
                  <Text t="inTheBalanceLoadingInstructionYouWillCreate" />
                </span>
                <CustomFormItem className="form-item-select-formItem" name="startDay">
                  <div className="form-item-select-formItem-title">
                    <span>
                      <strong>
                        <Text t="ofEachMonth" />
                      </strong>
                    </span>
                  </div>
                  {selectDay() !== undefined ? (
                    <Tooltip
                      overlayClassName="custom-tooltip"
                      placement="right"
                      title={
                        <div>
                          {selectDay()}
                          <button
                            type="button"
                            onClick={() => setTooltipVisible(false)}
                            aria-label="Close"
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              font: 'inherit',
                            }}
                          >
                            X
                          </button>
                        </div>
                      }
                      visible={
                        selectedStartDay === 0 || currentDate?.date() < totalAutoLoading?.startDay
                          ? false
                          : tooltipVisible
                      }
                    >
                      <CustomSelect
                        id="ticketRestaurant-autoUploadInstructions-instructionInformations-additionalInformation-select"
                        placeholder={selectChoose}
                        className="orderType"
                        onChange={onWhichDay}
                        defaultValue={selectedStartDay}
                        value={selectedStartDay}
                      >
                        <Option
                          id={`ticketRestaurant-autoUploadInstructions-instructionInformations-additionalInformation-chooseDay-option`}
                          value={0}
                        >
                          <Text t="choose" />
                        </Option>
                        {arrayDays.map((day) => (
                          <Option
                            id={`ticketRestaurant-autoUploadInstructions-instructionInformations-additionalInformation-${day}-option`}
                            key={day}
                            value={day}
                          >
                            {day}.
                          </Option>
                        ))}
                      </CustomSelect>
                    </Tooltip>
                  ) : (
                    <CustomSelect
                      id="ticketRestaurant-autoUploadInstructions-instructionInformations-additionalInformation-select"
                      placeholder={selectChoose}
                      className="orderType"
                      onChange={onWhichDay}
                      defaultValue={selectedStartDay}
                      value={selectedStartDay}
                    >
                      {arrayDays.map((day) => (
                        <Option
                          id={`ticketRestaurant-autoUploadInstructions-instructionInformations-additionalInformation-${day}-option`}
                          key={day}
                          value={day}
                        >
                          {day}.
                        </Option>
                      ))}
                    </CustomSelect>
                  )}
                  <div className="form-item-select-formItem-last-word">
                    <span className="form-item-select-formItem-last-word-help-title">
                      <Text t="day4" />
                    </span>
                  </div>
                </CustomFormItem>
              </div>
            </div>
          </CustomForm>
        </div>
      </CustomCollapseCard>
      <Alert
        banner
        showIcon={false}
        className="custom-alert-warning"
        closable={isMobil}
        message={
          <div className="alert-message-contain">
            <div className="alert-message-contain-icon">
              <CustomImage src={warning} />
            </div>
            <div className="alert-message-contain-description">
              <ul>
                <li>
                  <Text t="ifYouCreateAnAutomaticLoadingInstructionAn" />
                </li>
                <li>
                  <Text t="ifYouCancelOrBlockTheCardsYou" />
                </li>
                <li>
                  <Text t="forCardsThatAreCancelledAndNewlyProduced" />
                </li>
              </ul>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default AdditionalInformation;
